import React, { useState } from 'react';
import { FaTrophy, FaLock, FaCheck, FaFilter } from 'react-icons/fa';
import * as FaIcons from 'react-icons/fa';
import Modal from '../common/Modal';
import { ACHIEVEMENTS, ACHIEVEMENT_CATEGORIES } from '../../utils/gameData';

const AchievementsPanel = ({ isOpen, onClose, unlockedIds = {} }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const unlockedCount = Object.keys(unlockedIds).length;
  const totalCount = ACHIEVEMENTS.length;
  const progress = Math.round((unlockedCount / totalCount) * 100);

  // 按分類過濾成就
  const filteredAchievements = selectedCategory === 'all'
    ? ACHIEVEMENTS
    : ACHIEVEMENTS.filter(a => a.category === selectedCategory);

  // 計算每個分類的進度
  const getCategoryProgress = (category) => {
    const categoryAchievements = ACHIEVEMENTS.filter(a => a.category === category);
    const unlockedInCategory = categoryAchievements.filter(a => unlockedIds[a.id]).length;
    return {
      unlocked: unlockedInCategory,
      total: categoryAchievements.length
    };
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="成就收集" size="lg">
      {/* 總進度 */}
      <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-gray-800">解鎖進度</h3>
          <span className="text-2xl font-bold text-amber-600">
            {unlockedCount} / {totalCount}
          </span>
        </div>
        <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 transition-all duration-500 flex items-center justify-center text-white text-sm font-bold"
            style={{ width: `${progress}%` }}
          >
            {progress > 10 && `${progress}%`}
          </div>
        </div>
      </div>

      {/* 分類過濾器 */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <FaFilter className="text-gray-600" />
          <span className="text-sm font-bold text-gray-700">分類篩選</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
              selectedCategory === 'all'
                ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            全部 ({totalCount})
          </button>
          {Object.entries(ACHIEVEMENT_CATEGORIES).map(([key, category]) => {
            const progress = getCategoryProgress(key);
            const CategoryIcon = FaIcons[category.icon];
            return (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
                  selectedCategory === key
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {CategoryIcon && <CategoryIcon size={14} />}
                {category.name}
                <span className="text-xs opacity-80">
                  ({progress.unlocked}/{progress.total})
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 成就列表 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {filteredAchievements.map(achievement => {
          const Icon = FaIcons[achievement.icon];
          const isUnlocked = unlockedIds[achievement.id];
          const category = ACHIEVEMENT_CATEGORIES[achievement.category];

          return (
            <div
              key={achievement.id}
              className={`
                relative p-5 rounded-2xl transition-all duration-300
                ${
                  isUnlocked
                    ? 'bg-gradient-to-br from-yellow-100 via-amber-50 to-orange-50 border-2 border-yellow-400 shadow-lg hover:shadow-xl'
                    : 'bg-gray-100 border-2 border-gray-300 opacity-70 hover:opacity-80'
                }
              `}
            >
              <div className="flex items-start gap-4">
                {/* 圖標 */}
                <div
                  className={`
                    w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all
                    ${
                      isUnlocked
                        ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-md'
                        : 'bg-gray-300 text-gray-500'
                    }
                  `}
                >
                  {isUnlocked ? Icon && <Icon size={28} /> : <FaLock size={24} />}
                </div>

                {/* 內容 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-base text-gray-800">
                      {achievement.name}
                    </h3>
                    {category && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                        {category.name}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {achievement.description}
                  </p>
                </div>
              </div>

              {/* 解鎖徽章 */}
              {isUnlocked && (
                <div className="absolute top-3 right-3">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                    <FaCheck className="text-white" size={16} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 分類說明 */}
      {selectedCategory !== 'all' && (
        <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <p className="text-sm text-blue-800 text-center">
            {ACHIEVEMENT_CATEGORIES[selectedCategory].description}
          </p>
        </div>
      )}
    </Modal>
  );
};

export default AchievementsPanel;